import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'components/ui/dialog';
import { ColumnsDropdown } from '../common/ColumnDropdown';
import { useState } from 'react';
import { billingItemsColumns } from '../common/ColumnDropdown/utils';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { isEmail } from 'lib/is-email';
import { EmailTagInput } from 'components/EmailTagInput';
import { Loader2 } from 'lucide-react';
import { useMutation } from '@apollo/client';
import { EXPORT } from 'app/graphql';
import { ExportColumns } from 'app/graphql/generated/admin/graphql';
import { toast } from 'sonner';

type Props = {
  startDate: string;
  endDate: string;
};

export const ExportBillingItems = ({ startDate, endDate }: Props) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<ExportColumns[]>(
    Object.keys(billingItemsColumns) as ExportColumns[],
  );

  const handleSetSelectedColumns = (columns: ExportColumns[]) => {
    setSelectedColumns(columns);
  };

  const [exportBillingItems] = useMutation(EXPORT, {
    context: { clientName: 'admin' },
  });

  const handleExportSubmit = async () => {
    if (selectedColumns.length === 0) {
      toast.error('Please select at least one column to export', {
        id: 'export-billing-period-failed',
      });
    }
    setIsSubmitting(true);
    try {
      await exportBillingItems({
        variables: {
          input: {
            columns: selectedColumns,
            startDate,
            endDate,
            additionalRecipients: emails,
          },
        },
      });
      setOpen(false);
      setEmails([]);
      setSelectedColumns(Object.keys(billingItemsColumns) as ExportColumns[]);
      toast.success('Request to export billing period successful', {
        id: 'export-billing-period-success',
      });
    } catch (error) {
      console.error(error);
      toast.error('Error requesting export for billing period', {
        id: 'export-billing-period-failed',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Modal Dialog uses the shared backdrop from components/ui/dialog —
  // same translucent ink + soft blur as Notification + Payment dialogs.
  // Previously this used `modal={false}` with a hand-rolled black/80
  // overlay; the ColumnsDropdown still works inside the standard modal.
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {/* Export is a utility action — outline, not the lime CTA. */}
        <Button type="button" variant="outline" size="sm" className="ml-auto hidden lg:flex">
          Export
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Export billing period</DialogTitle>
          <DialogDescription>
            An email with a download link will be sent to the email on this account.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label>Columns</Label>
          <ColumnsDropdown
            onColumnChange={handleSetSelectedColumns}
            selectedColumns={selectedColumns}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label>Additional recipients</Label>
          <EmailTagInput
            emails={emails}
            setEmails={setEmails}
            disabled={isSubmitting}
            readOnly={isSubmitting}
            addTagsOnBlur={true}
            inputProps={{ inputMode: 'email' }}
            placeholder="Add email address"
            styleClasses={{
              input: 'w-full sm:max-w-[350px] shadow-none',
              tag: {
                body: 'bg-[var(--surface-2)] text-[var(--text)] border border-[var(--line)] hover:bg-[var(--surface-2)]',
              },
              tagList: { container: 'flex-wrap' },
            }}
            variant="primary"
            validateTag={(tag: string) => {
              return isEmail(tag);
            }}
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            disabled={isSubmitting || selectedColumns.length === 0}
            onClick={handleExportSubmit}
          >
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Export'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
